/**
 * Badge counts for the "Todos" (moderation) nav group: how many posts/comments are
 * awaiting a reply, and how many replies are in flight in the Outbox queue.
 *
 * (The old win-back Queue / Pipeline badges were retired with those surfaces — the
 * CRM Pipelines board owns win-backs now and is not badged in the sidebar.)
 *
 * WHAT THIS USED TO COST (doc 02 W2, doc 04 §4.2). A request on EVERY navigation,
 * forever, per counter — `limit: 1` list calls, the client asking a list endpoint
 * for a count and paying the list endpoint's full cost; `/admin/moderation/inbox?
 * limit=1` still ran the whole post ∪ comment union plus `moderation_metrics`. None
 * of it was cached, because this module had no `loaded` flag — which is doc 02
 * §6.2's fourth and fifth unconditional refetch.
 *
 * WHAT IT COSTS NOW. Nothing, on a normal navigation. The counts are the `badges`
 * block of `GET /v1/me`, computed server-side as a `SELECT count(*)` each over the
 * same `domain.*` predicates the lists use, and they arrive with the bootstrap the
 * page was going to issue anyway. They are refreshed when they can actually have
 * moved:
 *
 *   * a mutation that moves a counter RETURNS the new badges → `applyBadges`, 0 requests
 *   * the viewer switches community                          → `ensureTodoCounts`, 1 request
 *   * anything else that wants a resync                      → `loadTodoCounts`, 1 request
 */
import { localDayStartISO } from './admin';
import type { Badges } from '@api';
import { fetchBootstrap } from './session';
import { ensureBootstrap } from './session';

export const todos = $state<{
  /** The community these counts describe — null until the first load. Read it
   *  before trusting the numbers when a switch may be in flight. */
  skoolId: number | null;
  inbox: number;
  outbox: number;
  /** The local-day start the inbox count was computed for. The inbox badge shows
   *  TODAY's open items, so the counts are day-scoped as well as community-scoped. */
  day: string | null;
  loaded: boolean;
}>({ skoolId: null, inbox: 0, outbox: 0, day: null, loaded: false });

let inFlight: number | null = null;

/**
 * Adopt a `badges` block a v1 route just handed us — from the bootstrap, or from
 * the response to a write that moved a counter:
 *
 *     const res = await clearAllModeration(skoolId, filter);   // {affected, items, badges}
 *     applyBadges(skoolId, res.badges);
 *
 * That is the whole of doc 04 §4.2's second half: `POST …/moderation/items/actions/clear`,
 * `PATCH …/moderation/items/{kind}/{id}` and `DELETE …/outbox/items/{id}` all
 * return the new counts, so "mark all as answered" is one request against today's
 * six (W7) and the sidebar never refetches to find out what it already knows.
 */
export function applyBadges(skoolId: number, badges: Badges): void {
  todos.skoolId = skoolId;
  todos.inbox = badges.inbox;
  todos.outbox = badges.outbox;
  todos.day = localDayStartISO();
  todos.loaded = true;
}

/**
 * The counts for a community, WITHOUT a request when we already have them.
 *
 * This is what the sidebar calls on every mount. On the first mount of a page load
 * it takes the badges off the bootstrap — which is in flight already, so it costs
 * nothing — and on every subsequent navigation it returns immediately. Only a
 * genuine change of community (or of local day) reaches the network.
 */
export async function ensureTodoCounts(skoolId: number): Promise<void> {
  if (todos.loaded && todos.skoolId === skoolId && todos.day === localDayStartISO()) return;
  if (!todos.loaded) {
    // First load of this page: the bootstrap already carries these four counts for
    // the community it resolved. Free.
    const boot = await ensureBootstrap().catch(() => null);
    if (boot?.badges && boot.selected_skool_id === skoolId) {
      applyBadges(skoolId, boot.badges);
      return;
    }
  }
  await loadTodoCounts(skoolId);
}

/**
 * Refetch the counts for a community, unconditionally — one request, not four.
 *
 * The explicit resync path, for a caller that has changed something and does not
 * have the new badges in hand. A v1 mutation DOES hand them over, so prefer
 * `applyBadges`; this exists for the writes still on the legacy surface and for a
 * community switch.
 *
 * Concurrent/duplicate calls for the same id are coalesced; a newer id supersedes
 * an in-flight one. Fail-soft: prior counts stay put, the badge just doesn't move.
 */
export async function loadTodoCounts(skoolId: number): Promise<void> {
  if (inFlight === skoolId) return;
  inFlight = skoolId;
  try {
    // Uncached and community-specific on purpose: this must not adopt (or clobber)
    // the session bootstrap, whose `selected_skool_id` may be a different community.
    const boot = await fetchBootstrap(skoolId);
    if (inFlight !== skoolId) return; // a newer selection won; drop the stale result
    if (boot.badges) applyBadges(skoolId, boot.badges);
  } catch {
    /* leave prior counts in place; the badge just won't update this time */
  } finally {
    if (inFlight === skoolId) inFlight = null;
  }
}
